// WyngWingMark — the iconic WYNG wing visual, used everywhere a Wings
// balance, cosmetic preview or prestige cue is shown. Renders the official
// brand polygons (same source as PathStrip's `WyngLogoFragments`) so the
// icon is *pixel-identical* to the marketing logo, scaled to any size.
//
//   wings={1} → single (left) wing — first-tier state
//   wings={2} → full logo, both wings — second wing earned
//   dimmed=true → right wing softly fades (slump indicator); never removed
//
// `color` overrides the gradient fill (used by cosmetic skin themes like
// Aurora Halo / Solar Flare). When unset, we render in brand gold.

import { motion, useReducedMotion } from "motion/react";

// Polygon points from /public/brand/golden_wings_detached_6_polygons.svg.
// Keep in sync with components/path/WyngLogoFragments.jsx.
const SMALL_RIGHT = "970,555 850,592 823,620 812,650 835,622 921,597 950,578";
const SMALL_LEFT  = "564,555 600,590 630,603 691,617 707,627 721,646 710,617 681,590";
const BIG_RIGHT   = "1063,361 1011,407 878,483 834,521 806,576 796,675 814,616 839,583 875,565 969,541 1018,506 1048,453 1005,486 874,539 825,579 845,548 876,522 996,468 1035,442 1056,420 1072,384 1059,415 1042,431 1060,407 1071,375 1069,369 1060,368 1066,367";
const BIG_LEFT    = "459,348 464,352 460,359 471,396 492,428 475,409 501,441 543,470 662,525 694,554 709,583 690,561 659,539 544,493 500,466 488,452 516,504 558,538 664,567 697,586 719,614 739,675 731,580 722,552 702,521 655,481 570,437 489,382 535,455 506,421 473,363";

export default function WyngWingMark({
    wings = 1,
    dimmed = false,
    size = 28,
    color,        // optional override (cosmetic skins)
    glow,
    animate,
}) {
    const reduce = useReducedMotion();
    const wantGlow    = glow    ?? wings === 2;
    const wantBreathe = animate ?? (wings === 2 && !dimmed && !reduce);
    const rightOpacity = wings === 2 ? (dimmed ? 0.25 : 1) : 0;

    // Unique gradient id per render so multiple WyngWingMarks on screen
    // don't collide their <defs>.
    const gradId = `wmg-${Math.random().toString(36).slice(2, 8)}`;
    const fill = color || `url(#${gradId})`;
    const stroke = color || "#f6e58d";

    return (
        <motion.svg
            width={size}
            height={Math.round(size * (1024 / 1536))}
            viewBox="0 0 1536 1024"
            fill="none"
            aria-hidden
            data-wings={wings}
            data-dimmed={dimmed || undefined}
            initial={false}
            animate={wantBreathe ? { scale: [1, 1.04, 1] } : { scale: 1 }}
            transition={wantBreathe
                ? { duration: 3.2, repeat: Infinity, ease: "easeInOut" }
                : { duration: 0.4 }}
            style={{
                filter: wantGlow
                    ? `drop-shadow(0 0 ${Math.round(size * 0.35)}px var(--wyng-glow, rgba(255,214,10,0.55)))`
                    : "none",
            }}
        >
            {!color && (
                <defs>
                    <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%"   stopColor="#fff7c2" />
                        <stop offset="25%"  stopColor="#f4d35e" />
                        <stop offset="50%"  stopColor="#c58a11" />
                        <stop offset="75%"  stopColor="#f4d35e" />
                        <stop offset="100%" stopColor="#fff2a6" />
                    </linearGradient>
                </defs>
            )}

            {/* LEFT wing — always visible (single-wing baseline state). */}
            <polygon points={BIG_LEFT}  fill={fill} stroke={stroke}
                     strokeWidth={1.2} opacity={0.96} />
            <polygon points={SMALL_LEFT} fill={fill} stroke={stroke}
                     strokeWidth={1.2} opacity={0.96} />

            {/* RIGHT wing — appears once the second wing has been earned;
                fades to 25% opacity during a slump (dimmed=true). */}
            <g
                opacity={rightOpacity}
                style={{ transition: "opacity 0.6s ease" }}
            >
                <polygon points={BIG_RIGHT}  fill={fill} stroke={stroke}
                         strokeWidth={1.2} />
                <polygon points={SMALL_RIGHT} fill={fill} stroke={stroke}
                         strokeWidth={1.2} />
            </g>
        </motion.svg>
    );
}
